import { AIBlockConfig, AIGenerateOptions, AIEditOptions, AIEditBlockResponse } from "./ai-block-types";
import { NewElementData } from "shared/datamodel/canvas-element";
import { generateAIBlock } from "frontend/api";

/**
 * Singleton service for managing AI blocks
 */
export class AIBlockService {
  private static instance: AIBlockService;
  private blocks = new Map<string, AIBlockConfig>();

  static getInstance(): AIBlockService {
    if (!AIBlockService.instance) {
      AIBlockService.instance = new AIBlockService();
    }
    return AIBlockService.instance;
  }

  registerBlock(name: string, config: AIBlockConfig): void {
    this.blocks.set(name, config);
  }

  getBlock(name: string): AIBlockConfig | undefined {
    return this.blocks.get(name);
  }

  getAllBlocks(): AIBlockConfig[] {
    return [...this.blocks.values()];
  }

  shouldCreateIntegration(blockName: string): boolean {
    const block = this.getBlock(blockName);
    if (!block) {
      throw new Error(`AI block "${blockName}" not found`);
    }
    if (block.blockType === "edit") {
      return false;
    }
    return block.shouldCreateIntegration ?? false;
  }

  async generate(
    blockName: string,
    options: AIGenerateOptions
  ): Promise<{ elements: NewElementData[]; metadata?: Record<string, unknown> }> {
    const block = this.getBlock(blockName);
    if (!block) {
      throw new Error(`AI block "${blockName}" not found`);
    }
    if (block.blockType !== "generate") {
      throw new Error(`AI block "${blockName}" is not a generate block`);
    }

    try {
      // Show progress
      options.onProgress?.("Generating content...");
      const response = await this.getResponse(block, options);

      options.onProgress?.("Converting to canvas elements...");
      const position = options.position || block.defaultPosition || { x: 100, y: 100 };
      const { elements, metadata } = await block.responseToElements(response, position);

      options.onProgress?.("Complete!");

      options.onSuccess?.(blockName, options.prompt);

      return { elements, metadata };
    } catch (error) {
      console.error(`Error generating ${blockName}:`, error);
      throw error;
    }
  }

  async generateEdits(blockName: string, options: AIEditOptions): Promise<{ edits: AIEditBlockResponse[] }> {
    const block = this.getBlock(blockName);
    if (!block) {
      throw new Error(`AI block "${blockName}" not found`);
    }
    if (block.blockType !== "edit") {
      throw new Error(`AI block "${blockName}" is not an edit block`);
    }

    try {
      // Show progress
      options.onProgress?.("Generating edits...");
      const response = await this.getResponse(block, options);

      options.onProgress?.("Converting to element edits...");
      const { edits } = await block.responseToEdits(response, options);

      options.onProgress?.("Complete!");
      return { edits };
    } catch (error) {
      console.error(`Error generating edits for ${blockName}:`, error);
      throw error;
    }
  }

  private async getResponse(block: AIBlockConfig, options: AIGenerateOptions | AIEditOptions) {
    let response;
    if (block.apiCall) {
      response = await block.apiCall(options.prompt, options);
    } else {
      // Default to generateAIBlock, extracting options that apply
      const templateId = "templateId" in options ? (options as AIGenerateOptions).templateId : undefined;
      const boardContext = "boardContext" in options ? (options as AIGenerateOptions).boardContext : undefined;
      response = await generateAIBlock(block.name, options.prompt, options.chatHistory, templateId, boardContext);
    }

    return response;
  }
}
